/**
 * EditorialServicesPanel.jsx — Phase 2 of Human Editorial Services.
 *
 * Author-facing panel for:
 *  1. Building a live quote (manuscript type, stage, urgency, specialist)
 *  2. Confirming and checking out via Stripe
 *  3. Viewing order history + status badges
 *
 * Data testids:
 *  editorial-panel, stage-select, urgency-select, manuscript-type-select,
 *  specialist-toggle, word-count-input, quote-display, request-quote-btn,
 *  proceed-to-checkout-btn, order-history-list, order-item-{id}
 */
import React, { useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { usePW } from '@/lib/store';
import { Badge }  from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label }  from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch }   from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ExternalLink, RefreshCw, FileEdit, CheckCircle2, ClipboardList, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PanelFrame } from '../../workspace/PanelFrame';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtUSD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const STAGE_LABELS = {
  proofread:      'Proofreading',
  copyedit:       'Copyediting',
  developmental:  'Developmental Edit',
  full_pipeline:  'Full Editorial Pipeline',
};
const URGENCY_LABELS = {
  standard:   'Standard (14–21 days)',
  expedited:  'Expedited (7–10 days)',
  rush:        'Rush (3–5 days)',
};
const STATUS_COLORS = {
  pending:     'bg-yellow-100 text-yellow-800 border-yellow-200',
  paid:        'bg-blue-100   text-blue-800   border-blue-200',
  in_review:   'bg-purple-100 text-purple-800 border-purple-200',
  in_progress: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  delivered:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  complete:    'bg-green-100  text-green-800  border-green-200',
  cancelled:   'bg-slate-100  text-slate-600   border-slate-200',
};

// Status progression for the timeline strip
const STATUS_STEPS = ['pending', 'paid', 'in_review', 'in_progress', 'delivered', 'complete'];
const STEP_LABELS  = {
  pending:     'Submitted',
  paid:        'Confirmed',
  in_review:   'Under Review',
  in_progress: 'In Progress',
  delivered:   'Delivered',
  complete:    'Complete',
};

function StatusTimeline({ status }) {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
        Cancelled
      </div>
    );
  }
  const currentIdx = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-0.5 mt-1" data-testid="status-timeline">
      {STATUS_STEPS.map((s, i) => {
        const done    = i <= currentIdx;
        const current = i === currentIdx;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <div className={`w-2.5 h-2.5 rounded-full border transition-colors ${
                done
                  ? current ? 'bg-rose-700 border-rose-700' : 'bg-rose-300 border-rose-300'
                  : 'bg-background border-muted-foreground/30'
              }`} />
              {current && (
                <span className="text-[9px] text-rose-700 font-semibold whitespace-nowrap mt-0.5">
                  {STEP_LABELS[s]}
                </span>
              )}
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={`h-px w-4 flex-1 ${done && i < currentIdx ? 'bg-rose-300' : 'bg-muted-foreground/20'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Manuscript type descriptions (Phase 4 Non-Fiction Support) ───────────────
const TYPE_META = {
  fiction: {
    label: 'Fiction',
    desc: 'Novels, novellas, short story collections. Our editors focus on narrative flow, character voice, dialogue, pacing, and genre conventions.',
    rates: { proofread: '$0.010', copyedit: '$0.017', developmental: '$0.026', full_pipeline: '$0.048' },
    accent: 'bg-indigo-50 border-indigo-100 text-indigo-800',
  },
  trade_nf: {
    label: 'Trade Non-Fiction',
    desc: 'Memoirs, self-help, business, narrative non-fiction. Editors ensure factual clarity, logical argument structure, and reader accessibility.',
    rates: { proofread: '$0.016', copyedit: '$0.025', developmental: '$0.034', full_pipeline: '$0.067' },
    accent: 'bg-amber-50 border-amber-100 text-amber-800',
  },
  academic: {
    label: 'Academic',
    desc: 'Theses, academic papers, textbooks, technical research. Specialist editors with subject-area expertise ensure citation accuracy, argument precision, and style-guide compliance.',
    rates: { proofread: '$0.017', copyedit: '$0.026', developmental: '$0.042', full_pipeline: '$0.079' },
    accent: 'bg-emerald-50 border-emerald-100 text-emerald-800',
  },
};

function ManuscriptTypeExplainer({ type, stage }) {
  const meta = TYPE_META[type];
  if (!meta) return null;
  const rate = meta.rates[stage];
  return (
    <div
      data-testid="manuscript-type-explainer"
      className={`rounded-md border px-3 py-2.5 text-[12px] ${meta.accent}`}
    >
      <p className="font-semibold mb-0.5">{meta.label}</p>
      <p className="leading-relaxed mb-1">{meta.desc}</p>
      {rate && (
        <p className="font-medium opacity-80">
          Base rate for {STAGE_LABELS[stage]}: <span className="font-bold">{rate}/word</span>
          {' '}· platform 20 % · editor payout 80 %
        </p>
      )}
    </div>
  );
}

// ── Quote builder ─────────────────────────────────────────────────────────────
function QuoteBuilder({ project, onOrderCreated }) {
  const [manuscriptType, setManuscriptType] = useState('fiction');
  const [stage,          setStage]          = useState('copyedit');
  const [urgency,        setUrgency]        = useState('standard');
  const [specialist,     setSpecialist]     = useState(false);
  const [notes,          setNotes]          = useState('');
  const [wordCount,      setWordCount]      = useState('');
  const [quote,          setQuote]          = useState(null);
  const [orderId,        setOrderId]        = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [checkoutLoading,setCheckoutLoading]= useState(false);

  // auto-fill word count from active project
  useEffect(() => {
    if (project?.chapters) {
      const total = project.chapters.reduce((sum, c) => sum + (c.words || 0), 0);
      if (total > 0) setWordCount(String(total));
    }
  }, [project]);

  const requestQuote = useCallback(async () => {
    const wc = parseInt(wordCount, 10);
    if (!wc || wc < 1) { toast.error('Enter a valid word count'); return; }
    setLoading(true);
    try {
      const res = await http.post('/editorial/request', {
        manuscript_type: manuscriptType,
        stage, urgency, specialist,
        word_count: wc,
        notes: notes || undefined,
        project_id: project?.id || undefined,
      });
      if (res.data?.ok) {
        setQuote(res.data.order);
        setOrderId(res.data.order.id);
        onOrderCreated?.();
        toast.success('Quote created — review below');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate quote');
    } finally {
      setLoading(false);
    }
  }, [manuscriptType, stage, urgency, specialist, notes, wordCount, project, onOrderCreated]);

  const proceedToCheckout = useCallback(async () => {
    if (!orderId) return;
    setCheckoutLoading(true);
    try {
      const res = await http.post(`/editorial/checkout/${orderId}`);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment gateway unavailable');
      setCheckoutLoading(false);
    }
  }, [orderId]);

  const reset = () => { setQuote(null); setOrderId(null); };

  return (
    <div className="space-y-4" data-testid="quote-builder">
      {/* Manuscript Type */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[13px]">Manuscript type</Label>
          <Select value={manuscriptType} onValueChange={setManuscriptType}>
            <SelectTrigger data-testid="manuscript-type-select" className="h-8 text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fiction">Fiction</SelectItem>
              <SelectItem value="trade_nf">Trade Non-fiction</SelectItem>
              <SelectItem value="academic">Academic</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[13px]">Editing stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="stage-select" className="h-8 text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STAGE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Manuscript type explainer */}
      <ManuscriptTypeExplainer type={manuscriptType} stage={stage} />

      {/* Urgency + Word count */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[13px]">Urgency</Label>
          <Select value={urgency} onValueChange={setUrgency}>
            <SelectTrigger data-testid="urgency-select" className="h-8 text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(URGENCY_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[13px]">Word count</Label>
          <input
            data-testid="word-count-input"
            type="number"
            min="1"
            value={wordCount}
            onChange={(e) => setWordCount(e.target.value)}
            className="flex h-8 w-full rounded-md border border-input bg-background px-3 text-[13px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="e.g. 80000"
          />
        </div>
      </div>

      {/* Specialist toggle */}
      <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2.5">
        <div>
          <p className="text-[13px] font-medium">Genre specialist</p>
          <p className="text-[12px] text-muted-foreground">Request an editor with expertise in your genre (+20%)</p>
        </div>
        <Switch
          data-testid="specialist-toggle"
          checked={specialist}
          onCheckedChange={setSpecialist}
        />
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <Label className="text-[13px]">Notes for your editor (optional)</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="text-[13px] resize-none"
          placeholder="Any special requirements, audience, concerns..."
        />
      </div>

      {/* Quote display */}
      {quote && (
        <div
          data-testid="quote-display"
          className="rounded-md border-2 border-rose-200 bg-rose-50/60 px-4 py-3 space-y-1"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Your quote</p>
          <p className="text-2xl font-bold text-rose-900">{fmtUSD(quote.quoted_usd)}</p>
          <p className="text-[12.5px] text-rose-700">
            {STAGE_LABELS[quote.stage]} · {URGENCY_LABELS[quote.urgency]}
            {quote.specialist ? ' · Genre specialist' : ''} · {quote.word_count.toLocaleString()} words
          </p>
          <p className="text-[11px] text-muted-foreground pt-1">
            Secure Stripe checkout — your card is not charged until you confirm.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {!quote ? (
          <Button
            data-testid="request-quote-btn"
            onClick={requestQuote}
            disabled={loading}
            className="flex-1"
          >
            {loading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Get My Quote
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={reset} className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Adjust
            </Button>
            <Button
              data-testid="proceed-to-checkout-btn"
              onClick={proceedToCheckout}
              disabled={checkoutLoading}
              className="flex-1 gap-1.5 bg-rose-700 hover:bg-rose-800"
            >
              {checkoutLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <ExternalLink className="h-3.5 w-3.5" />}
              Confirm &amp; Pay {fmtUSD(quote.quoted_usd)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Order history ─────────────────────────────────────────────────────────────
function OrderHistory({ refreshKey }) {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/editorial/orders');
      setOrders(res.data?.orders || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return (
    <div className="flex items-center justify-center py-8 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading orders…
    </div>
  );
  if (!orders.length) return (
    <p className="py-6 text-center text-[13px] text-muted-foreground">No editorial orders yet.</p>
  );

  return (
    <ul data-testid="order-history-list" className="divide-y">
      {orders.map((o) => (
        <li key={o.id} data-testid={`order-item-${o.id}`} className="py-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold">
              {STAGE_LABELS[o.stage] || o.stage}
            </p>
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[o.status] || ''}`}>
              {o.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <p className="text-[12.5px] text-muted-foreground">
            {(o.manuscript_type || '').replace('_', ' ')} · {URGENCY_LABELS[o.urgency] || o.urgency}
            {o.specialist ? ' · Specialist' : ''} · {(o.word_count || 0).toLocaleString()} words
          </p>
          <p className="text-[13px] font-bold text-rose-800">{fmtUSD(o.quoted_usd)}</p>
          <StatusTimeline status={o.status} />
          {/* Resume payment — pending orders only */}
          {o.status === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[12px] gap-1"
              onClick={async () => {
                try {
                  const res = await http.post(`/editorial/checkout/${o.id}`);
                  if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Payment gateway unavailable');
                }
              }}
            >
              <ExternalLink className="h-3 w-3" /> Resume payment
            </Button>
          )}
          {/* View editor's inline markups — available once the editor has been assigned */}
          {['in_review', 'in_progress', 'delivered', 'complete'].includes(o.status) && o.editing_request_id && (
            <Button
              size="sm"
              className="h-7 text-[12px] gap-1"
              data-testid={`view-markups-btn-${o.id}`}
              onClick={() => navigate(`/editor/review/${o.editing_request_id}`)}
            >
              <MessageSquarePlus className="h-3 w-3" />
              View Editor&apos;s Markups
            </Button>
          )}
          {/* Delivered without a linked workspace yet */}
          {o.status === 'delivered' && !o.editing_request_id && (
            <div className="flex items-center gap-1 text-emerald-600 text-[12px]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Delivered — markups will appear here once the editor links them
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function EditorialServicesPanel({ onClose }) {
  const { project }           = usePW();
  const [tab,       setTab]   = useState('quote');
  const [refreshKey, setRefreshKey] = useState(0);

  const onOrderCreated = () => {
    setRefreshKey((k) => k + 1);
    setTimeout(() => setTab('orders'), 800);
  };

  return (
    <PanelFrame
      title="Human Editorial Services"
      icon={ClipboardList}
      onClose={onClose}
      panelId="editorial"
    >
      <div data-testid="editorial-panel" className="flex flex-col h-full overflow-auto p-4 space-y-4">
        {/* Sub-header description */}
        <p className="text-[12.5px] text-muted-foreground">
          Professional editors for your manuscript. Quote instantly — pay securely via Stripe.
        </p>

        {/* Tab bar */}
        <div className="flex rounded-md border overflow-hidden">
          {[['quote', 'Get a Quote'], ['orders', 'My Orders']].map(([v, l]) => (
            <button
              key={v}
              data-testid={`editorial-tab-${v}`}
              onClick={() => setTab(v)}
              className={`flex-1 py-1.5 text-[13px] font-semibold transition-colors ${
                tab === v
                  ? 'bg-rose-700 text-white'
                  : 'bg-background text-muted-foreground hover:bg-accent'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === 'quote' && (
          <QuoteBuilder project={project} onOrderCreated={onOrderCreated} />
        )}
        {tab === 'orders' && (
          <OrderHistory refreshKey={refreshKey} />
        )}
      </div>
    </PanelFrame>
  );
}
